import React, { useEffect, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import Select from "react-select";
import { get } from "aws-amplify/api";
import SLMAddressList from "./SLMAddressList";
import SLMSearchPageShell from "../SLMSearchPageShell";

function SLMAddressSearch({ isAuthenticated }) {
  const [isLoading, setIsLoading] = useState(true);
  const [stateOptions, setStateOptions] = useState([]);
  const [selectedState, setSelectedState] = useState(null);
  const [buildingName, setBuildingName] = useState("");
  const [fullAddress, setFullAddress] = useState("");
  const [street, setStreet] = useState("");
  const [streetext, setStreetext] = useState("");
  const [stnolow, setStnolow] = useState("");
  const [stnohigh, setStnohigh] = useState("");
  const [state, setState] = useState("");
  const [suburb, setSuburb] = useState("");
  const [postcode, setPostcode] = useState("");
  const [addressid, setAddressid] = useState("");
  const [hardseq, setHardseq] = useState("");
  const [floorunitno, setFloorunitno] = useState("");
  const [cityscoperef, setCityscoperef] = useState("");
  const [lotplan, setLotplan] = useState("");
  const [titleref, setTitleref] = useState("");
  const [addresses, setAddresses] = useState([]);
  const [searchCount, setSearchCount] = useState(0);
  const [filter, setFilter] = useState({});

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    document.title = "SLM Address Search";

    (async () => {
      try {
        const localStates = await getStates();
        setStateOptions(localStates || []);
      } catch (error) {
        alert(error + ". Please refresh the page (F5) to try again.");
      }
      setIsLoading(false);
    })();
  }, [isAuthenticated]);

  async function getStates() {
    const response = await get({
      apiName: "colin",
      path: "/slmstates",
    }).response;

    const states = await response.body.json();
    return states;
  }

  async function getAddresses(activeFilter) {
    const response = await get({
      apiName: "colin",
      path: "/slmaddresses",
      options: {
        queryParams: activeFilter,
      },
    }).response;

    const results = await response.body.json();
    return results;
  }

  const buildFilter = () => ({
    buildingname: buildingName,
    fulladdress: fullAddress,
    street,
    streetext,
    stnolow,
    stnohigh,
    floorunitno,
    state,
    suburb,
    addressid,
    hardseq,
    postcode,
    cityscoperef,
    lotplan,
    titleref,
  });

  const handleChange = (event) => {
    const { id, value } = event.target;

    if (id === "addressid") {
      const numeric = Number(value);
      if (!Number.isNaN(numeric) && (numeric < -2147483648 || numeric > 2147483647)) {
        alert("Not a valid address id");
        return;
      }
    }

    if (id === "buildingName") setBuildingName(value);
    if (id === "fullAddress") setFullAddress(value);
    if (id === "street") setStreet(value);
    if (id === "streetext") setStreetext(value);
    if (id === "stnolow") {
      setStnolow(value);
      if (stnohigh === "" || stnohigh === null || Number(stnohigh) < Number(value)) {
        setStnohigh(value);
      }
    }
    if (id === "stnohigh") setStnohigh(value);
    if (id === "suburb") setSuburb(value);
    if (id === "postcode") setPostcode(value);
    if (id === "addressid") setAddressid(value);
    if (id === "hardseq") setHardseq(value);
    if (id === "floorunitno") setFloorunitno(value);
    if (id === "cityscoperef") setCityscoperef(value);
    if (id === "lotplan") setLotplan(value);
    if (id === "titleref") setTitleref(value);
  };

  const handleStateChange = (nextState) => {
    if (nextState) {
      setSelectedState(nextState);
      setState(nextState.value);
    } else {
      setSelectedState(null);
      setState("");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);

    const activeFilter = buildFilter();

    try {
      const nextAddresses = await getAddresses(activeFilter);
      setAddresses(nextAddresses || []);
      setFilter(activeFilter);
      setSearchCount((count) => count + 1);
    } catch (error) {
      alert(error + ". Please refresh the page (F5) to try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const clearFilters = async () => {
    setBuildingName("");
    setFullAddress("");
    setStreet("");
    setStreetext("");
    setStnolow("");
    setStnohigh("");
    setState("");
    setSuburb("");
    setPostcode("");
    setAddressid("");
    setHardseq("");
    setFloorunitno("");
    setCityscoperef("");
    setLotplan("");
    setTitleref("");
    setSelectedState(null);
    
    // Fetch results with all filters explicitly set to empty strings (faster API response)
    setIsLoading(true);
    try {
      const emptyFilter = {
        buildingname: "",
        fulladdress: "",
        street: "",
        streetext: "",
        stnolow: "",
        stnohigh: "",
        floorunitno: "",
        state: "",
        suburb: "",
        addressid: "",
        hardseq: "",
        postcode: "",
        cityscoperef: "",
        lotplan: "",
        titleref: "",
      };
      const nextAddresses = await getAddresses(emptyFilter);
      setAddresses(nextAddresses || []);
      setFilter(emptyFilter);
      setSearchCount((count) => count + 1);
    } catch (error) {
      alert(error + ". Please refresh the page (F5) to try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const filters = (
    <>
      <Row className="mb-2 g-2">
        <Col>
          <Form.Group controlId="buildingName">
            <Form.Control type="input" value={buildingName} onChange={handleChange} placeholder="Building Name containing" />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-2 g-2">
        <Col xs={12} sm={6} md={4} lg={4}>
          <Form.Group controlId="stnolow">
            <Form.Control type="input" value={stnolow} onChange={handleChange} placeholder="St No. Low" />
          </Form.Group>
        </Col>

        <Col xs={12} sm={6} md={4} lg={4}>
          <Form.Group controlId="stnohigh">
            <Form.Control type="input" value={stnohigh} onChange={handleChange} placeholder="St No. High" />
          </Form.Group>
        </Col>

        <Col xs={12} sm={6} md={4} lg={4}>
          <Form.Group controlId="floorunitno">
            <Form.Control type="input" value={floorunitno} onChange={handleChange} placeholder="Unit" />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-2 g-2">
        <Col xs={12} sm={6} lg={8}>
          <Form.Group controlId="street">
            <Form.Control type="input" value={street} onChange={handleChange} placeholder="Street Name starting" />
          </Form.Group>
        </Col>
        <Col xs={12} sm={6} lg={4}>
          <Form.Group controlId="streetext">
            <Form.Control type="input" value={streetext} onChange={handleChange} placeholder="Street Ext" />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-2 g-2">
        <Col>
          <Form.Group controlId="suburb">
            <Form.Control type="input" value={suburb} onChange={handleChange} placeholder="Suburb starting" />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-2 g-2">
        <Col xs={12} sm={6} lg={8}>
          <Form.Group controlId="state">
            <Select value={selectedState} onChange={handleStateChange} options={stateOptions} placeholder="Select a state" isClearable />
          </Form.Group>
        </Col>
        <Col xs={12} sm={6} lg={4}>
          <Form.Group controlId="postcode">
            <Form.Control type="input" value={postcode} onChange={handleChange} placeholder="Postcode" />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-2 g-2">
        <Col xs={12} sm={6}>
          <Form.Group controlId="addressid">
            <Form.Control type="input" value={addressid} onChange={handleChange} placeholder="CoLin ID" />
          </Form.Group>
        </Col>
        <Col xs={12} sm={6}>
          <Form.Group controlId="hardseq">
            <Form.Control type="input" value={hardseq} onChange={handleChange} placeholder="PIMs ID" />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-2 g-2">
        <Col xs={12} sm={6}>
          <Form.Group controlId="lotplan">
            <Form.Control type="input" value={lotplan} onChange={handleChange} placeholder="Lot Plan containing" />
          </Form.Group>
        </Col>
        <Col xs={12} sm={6}>
          <Form.Group controlId="titleref">
            <Form.Control type="input" value={titleref} onChange={handleChange} placeholder="Title Reference containing" />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-2 g-2">
        <Col>
          <Form.Group controlId="cityscoperef">
            <Form.Control type="input" value={cityscoperef} onChange={handleChange} placeholder="Cityscope Reference starting" />
          </Form.Group>
        </Col>
      </Row>
    </>
  );

  if (!isAuthenticated) {
    return null;
  }

  return (
    <SLMSearchPageShell
      className="SLMAddressSearch"
      title="SLM Addresses"
      isLoading={isLoading}
      onSubmit={handleSubmit}
      onClear={clearFilters}
      filterDensity="tight"
      filterPanel={filters}
      resultsPanel={<SLMAddressList filter={filter} isAuthenticated={isAuthenticated} isLoading={isLoading} data={addresses} search_count={searchCount} />}
    />
  );
}

export default SLMAddressSearch;
